import { WarningSolid } from '@wfp/icons-react';
import {
  Button,
  Input,
  Module,
  ModuleBody,
  ModuleFooter,
  ModuleHeader,
} from '@wfp/react';
import type { FC } from 'react';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
  APPLY_CHANGE_TITLE_KEY,
  REPORT_DETAIL_DATA_KEY,
} from 'src/redux/constants';
import { ApplyChangeTitle } from 'src/redux/sections/actions';
import { RootState } from 'src/redux/store';
import { sendGAEvent } from 'src/utils';

interface ISubtitle {
  title: string;
  description: string;
}

interface IHeadingWidget {
  component: React.ReactNode;
}

interface Props {
  headingWidgets: IHeadingWidget[];
  initialContent: string;
  isEditable: boolean;
  sectionId: number;
  subtitle?: ISubtitle;
  isVersionsTab?: boolean;
}

interface PropsTitleErrorMessage {
  msg: string;
}

const maxCharacterCount = 30;

const Title: FC<Props> = ({
  headingWidgets = [],
  initialContent = '',
  isEditable = true,
  sectionId,
  subtitle = null,
  isVersionsTab = false,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const reportDetailData = useSelector(
    (state: RootState) => state.viewData[REPORT_DETAIL_DATA_KEY]?.data,
  );

  const changeTitleIsFetching = useSelector(
    (state: RootState) => state.viewData[APPLY_CHANGE_TITLE_KEY]?.isFetching,
  );

  const [isEdit, setIsEdit] = useState(false);
  const [content, setContent] = useState(initialContent || '');

  const charsExceed = content?.length > maxCharacterCount;
  const isEmpty = content.trim()?.length === 0;
  const isNotValid = getSectionNames()?.includes(content.trim());

  useEffect(() => {
    resetContent();
  }, [initialContent]);

  function resetContent() {
    setContent(initialContent);
  }

  function handleCancelBtn() {
    setIsEdit(false);
    resetContent();
  }

  function handleSaveBtn() {
    sendGAEvent('extra_section_title_save_btn_clicked');
    if (content !== initialContent) {
      dispatch(ApplyChangeTitle({ title: content }, sectionId));
      navigate({ pathname: '', hash: `#${content}` }, { replace: true });
    }
    setIsEdit(false);
  }

  function getCharactersResume() {
    return `${content?.length || 0} / ${maxCharacterCount}`;
  }

  function getSectionNames() {
    if (!reportDetailData?.sections) return [];

    const sectionNames: string[] = [];
    reportDetailData.sections.forEach((section: any) => {
      if (
        !['Country office story', 'Extra Section'].includes(
          section.custom_title,
        )
      ) {
        sectionNames.push(section.custom_title);
      }

      section.children_list?.forEach((sectionChild: any) => {
        sectionNames.push(sectionChild.custom_title);
      });
    });
    return sectionNames;
  }

  const TitleErrorMessage: FC<PropsTitleErrorMessage> = ({ msg }) => {
    return (
      <div className="subtitle-footer subtitle-error-msg">
        <WarningSolid color="#E4002B" />
        <div>{msg}</div>
      </div>
    );
  };

  if (isVersionsTab) {
    return (
      <p className="title">
        <strong>{initialContent}</strong>
      </p>
    );
  }

  return (
    <Module className="subtitle-section" data-test-id="title-section">
      <ModuleHeader data-test-id="section-title">
        <div className="subtitle-section-title">Title</div>
        {isEditable && (
          <div className="subtitle-section-btns">
            {isEdit ? (
              <>
                <Button onClick={handleCancelBtn} kind="secondary">
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveBtn}
                  disabled={
                    charsExceed ||
                    isEmpty ||
                    isNotValid ||
                    changeTitleIsFetching
                  }
                >
                  Save title
                </Button>
              </>
            ) : (
              <Button
                disabled={changeTitleIsFetching}
                onClick={() => setIsEdit(true)}
              >
                Edit
              </Button>
            )}
          </div>
        )}
      </ModuleHeader>
      <ModuleBody>
        <div className="module-body-title-wrapper">
          {isEdit ? (
            <Input name="title">
              <input
                value={content}
                className="subtitle-input"
                placeholder="Insert title"
                onChange={val => setContent(val.currentTarget.value)}
              />
            </Input>
          ) : initialContent ? (
            <div className="title-content-wrapper">{initialContent}</div>
          ) : (
            <div className="subtitle-empty-info">No title</div>
          )}

          {!isEdit && (
            <div className="subtitle-wrapper">
              {subtitle?.title && <p className="subtitle">{subtitle.title}</p>}
              {subtitle?.description && (
                <aside className="subtitle-note">{subtitle.description}</aside>
              )}
              <p className="title">
                {headingWidgets.map((item, index) => (
                  <span key={index}>: {item.component}</span>
                ))}
              </p>
            </div>
          )}
        </div>
      </ModuleBody>
      {isEditable && (
        <ModuleFooter>
          {charsExceed ? (
            <TitleErrorMessage
              msg={`Character limit exceed: ${getCharactersResume()}`}
            />
          ) : isEmpty ? (
            <TitleErrorMessage msg="The title cannot be empty" />
          ) : isNotValid ? (
            <TitleErrorMessage msg="This title is already used for another section" />
          ) : (
            <div className="subtitle-footer">
              Character count: {getCharactersResume()}
            </div>
          )}
        </ModuleFooter>
      )}
    </Module>
  );
};

export default Title;
